import React, { useState, useEffect, useCallback } from 'react';
import Loader from './Loader';
import { toast } from 'react-toastify';
import ApiService from '../services/ApiService';
import Swal from 'sweetalert2';
import { formatDate } from '../utils/helper';
import './BookingDetailsSidebar.css';

const GOLD = '#c4941a';

const STATUS_ACTIONS = {
  pending: [
    { value: 'Confirmed', label: 'Confirm Booking', icon: 'bi-check-circle', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking',  icon: 'bi-x-circle',     cls: 'bd-btn-danger'  },
  ],
  confirmed: [
    { value: 'Completed', label: 'Mark Completed', icon: 'bi-patch-check', cls: 'bd-btn-primary' },
    { value: 'Cancelled', label: 'Cancel Booking', icon: 'bi-x-circle',    cls: 'bd-btn-danger'  },
  ],
};

const statusPillClass = (status) => {
  const map = {
    pending:   'bd-status-pill bd-pending',
    confirmed: 'bd-status-pill bd-confirmed',
    completed: 'bd-status-pill bd-completed',
    cancelled: 'bd-status-pill bd-cancelled',
    refunded:  'bd-status-pill bd-refunded',
    active:    'bd-status-pill bd-confirmed',
  };
  return map[status?.toLowerCase()] || 'bd-status-pill bd-default';
};

const PartnerBookingDetailsSidebar = ({ bookingId, onUpdate }) => {
  const [isLoader, setIsLoader] = useState(false);
  const [booking, setBooking] = useState(null);

  const getBooking = useCallback(async () => {
    if (!bookingId) return;
    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'GET',
        url: `partner/getBookingDetails?booking_id=${bookingId}`,
      });
      if (res.data.status) setBooking(res.data.data);
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to load booking details');
    } finally {
      setIsLoader(false);
    }
  }, [bookingId]);

  useEffect(() => { getBooking(); }, [getBooking]);

  const handleStatusUpdate = async (newStatus) => {
    const b = booking;
    if (!b?._id) { toast.error('Booking not found'); return; }

    const result = await Swal.fire({
      title: `Mark as ${newStatus}`,
      text: `Update this booking status to ${newStatus}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: GOLD,
      confirmButtonText: `Yes, ${newStatus}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'POST',
        url: 'partner/updateBookingStatus',
        data: { booking_id: b._id, status: newStatus },
      });
      if (res.data.status) {
        toast.success(`Booking marked as ${newStatus}`);
        await getBooking();
        onUpdate?.();
      } else {
        toast.error(res.data.message || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update booking status');
    } finally {
      setIsLoader(false);
    }
  };

  const fmt = (amount) => `AED ${Number(amount ?? 0).toLocaleString()}`;

  const b = booking;
  const currentActions = STATUS_ACTIONS[b?.status?.toLowerCase()] || [];

  if (isLoader && !b) {
    return (
      <div className="d-flex justify-content-center align-items-center py-10">
        <Loader text="Loading..." size={35} />
      </div>
    );
  }

  if (!b) return null;

  const bookingCode = b.booking_code || `#${b._id?.toString().slice(-8)}`;
  const services = b.services || [];
  const serviceName = services[0]?.title || services[0]?.name || '—';
  const eventDateTime = b.event_date
    ? `${formatDate(b.event_date, 'MMM DD, YYYY')}${b.event_time ? ` at ${b.event_time}` : ''}`
    : 'N/A';

  const copyCode = () => {
    navigator.clipboard?.writeText(bookingCode);
    toast.success('Booking ID copied', { autoClose: 1500 });
  };

  return (
    <div className="booking-sidebar-wrapper">
      {isLoader && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader text="Processing" size={40} />
        </div>
      )}

      {/* Status + code */}
      <div className="bd-top-row">
        <span className={`${statusPillClass(b.status)} text-uppercase`}>{b.status || 'N/A'}</span>
        <button className="bd-code-btn" onClick={copyCode} title="Copy booking ID">
          {bookingCode}
          <i className="bi bi-clipboard"></i>
        </button>
      </div>

      {/* Customer */}
      <div className="bd-customer">
        <div className="bd-customer-avatar">
          <img
            src={b.customer?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.customer?.name || 'U')}&background=F1E9DA&color=B98A4B&size=128`}
            alt={b.customer?.name}
            onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(b.customer?.name || 'U')}&background=F1E9DA&color=B98A4B&size=128`; }}
          />
        </div>
        <div className="bd-customer-info">
          <div className="bd-customer-name">{b.customer?.name || 'N/A'}</div>
          <div className="bd-customer-line"><i className="bi bi-telephone"></i>{b.customer?.phone || 'N/A'}</div>
          <div className="bd-customer-line"><i className="bi bi-envelope"></i>{b.customer?.email || 'N/A'}</div>
        </div>
      </div>

      {/* Booking Information */}
      <div className="bd-section">
        <h6 className="bd-section-title">Booking Information</h6>
        <div className="bd-row">
          <span className="bd-label">Service</span>
          <span className="bd-value">{serviceName}</span>
        </div>
        {services.length > 1 && (
          <div className="bd-row">
            <span className="bd-label">Other Services</span>
            <span className="bd-value">{services.slice(1).map((s) => s.title || s.name).join(', ')}</span>
          </div>
        )}
        <div className="bd-row">
          <span className="bd-label">Event Date &amp; Time</span>
          <span className="bd-value">{eventDateTime}</span>
        </div>
        {b.address?.complete_address && (
          <div className="bd-row">
            <span className="bd-label">Location</span>
            <span className="bd-value">{b.address.complete_address}</span>
          </div>
        )}
      </div>

      {/* Payment Summary */}
      <div className="bd-section">
        <h6 className="bd-section-title">Payment Summary</h6>
        <div className="bd-row">
          <span className="bd-label">Subtotal</span>
          <span className="bd-value">{fmt(b.total_price)}</span>
        </div>
        {b.service_charges > 0 && (
          <div className="bd-row">
            <span className="bd-label">Service Charges</span>
            <span className="bd-value">{fmt(b.service_charges)}</span>
          </div>
        )}
        <div className="bd-row bd-total-row">
          <span className="bd-label bd-strong">Grand Total</span>
          <span className="bd-value bd-strong">{fmt(b.grand_total)}</span>
        </div>
        <div className="bd-row">
          <span className="bd-label">Payment Status</span>
          <span className={`bd-pay-pill ${String(b.payment_status).toLowerCase() === 'paid' ? 'bd-paid' : 'bd-unpaid'}`}>
            {b.payment_status || 'N/A'}
          </span>
        </div>
      </div>

      {/* Actions */}
      {currentActions.length > 0 && (
        <div className="bd-actions">
          <h6 className="bd-section-title">Actions</h6>
          {currentActions.map(({ value, label, icon, cls }) => (
            <button
              key={value}
              className={`bd-action-btn ${cls}`}
              disabled={isLoader}
              onClick={() => handleStatusUpdate(value)}
            >
              <i className={`bi ${icon} me-2`}></i>{label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartnerBookingDetailsSidebar;
